import { useQueryClient } from '@tanstack/react-query';

import { useSecretMutation } from '@/hooks/use-secret-mutation';

import { clientService } from '@/services/client-service';
import type { RotateClientSecretResult } from '@/types/core/client';
import type { UUID } from '@/utils/uuid';

import { clientMutationKeys, clientQueryKeys } from './query-keys';

/**
 * Hook to rotate a client's secret.
 * Returns the new clientSecret (shown once).
 * Invalidates the clients cache to trigger a refetch.
 */
export function useRotateClientSecret(tenantId?: UUID) {
  const qc = useQueryClient();

  return useSecretMutation<RotateClientSecretResult, UUID>({
    mutationKey: clientMutationKeys.rotateClientSecret(tenantId),
    mutationFn: async (clientId: UUID) => {
      if (!tenantId) throw new Error('Missing tenant id');
      return clientService.rotateClientSecret(tenantId, clientId);
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: clientQueryKeys.listByTenant(tenantId),
        refetchType: 'all',
      });
    },
  });
}
